import React from 'react';

/**
 * LanguagesShowcase — a full-screen radial display of the 13 languages
 * Falak supports. Used exclusively for the Play Store / AppGallery
 * screenshot #7. The Falak crescent sits at the centre, and 13 rays
 * radiate outward, each one carrying its language name written in its
 * own native script.
 *
 * Route: /languages-showcase
 * Ideal capture: 1080 × 1920 (portrait, mobile store dimensions).
 */

// 13 languages arranged so long-name languages sit on LONG rays (more
// breathing room) and short-name languages sit on SHORT rays. The 7/6
// alternating pattern (L S L S L S L S L S L S L) is a natural fit for the
// odd count of 13 — no forced symmetry break, just gentle visual rhythm.
// All rays share the same warm gold family (variations in luminosity
// only) — never rainbow — so they read as unified beams of light
// radiating from the Falak crescent.
const LANGUAGES = [
  // idx 0 · LONG · upper-right → shortest LONG name (Türkçe, 6 chars)
  { code: 'tr', native: 'Türkçe',            hue: '#EFCF88', dir: 'ltr', long: true  },
  // idx 1 · SHORT · right-upper
  { code: 'en', native: 'English',           hue: '#F0D08A', dir: 'ltr', long: false },
  // idx 2 · LONG · right → most horizontal room (Bahasa Indonesia = 16 chars)
  { code: 'id', native: 'Bahasa Indonesia',  hue: '#F5DFA0', dir: 'ltr', long: true  },
  // idx 3 · SHORT · right-lower
  { code: 'ur', native: 'اردو',               hue: '#F8E9BA', dir: 'rtl', long: false },
  // idx 4 · LONG · lower-right
  { code: 'fr', native: 'Français',          hue: '#F3D998', dir: 'ltr', long: true  },
  // idx 5 · SHORT · bottom-right
  { code: 'fa', native: 'فارسی',              hue: '#FBEBB0', dir: 'rtl', long: false },
  // idx 6 · LONG · bottom
  { code: 'pt', native: 'Português',         hue: '#E8C878', dir: 'ltr', long: true  },
  // idx 7 · SHORT · bottom-left
  { code: 'bn', native: 'বাংলা',              hue: '#F2D48A', dir: 'ltr', long: false },
  // idx 8 · LONG · lower-left
  { code: 'it', native: 'Italiano',          hue: '#F3D998', dir: 'ltr', long: true  },
  // idx 9 · SHORT · left-lower
  { code: 'zh', native: '中文',                 hue: '#F8E5A8', dir: 'ltr', long: false },
  // idx 10 · LONG · left → most horizontal room (Español)
  { code: 'es', native: 'Español',           hue: '#EAC98C', dir: 'ltr', long: true  },
  // idx 11 · SHORT · left-upper
  { code: 'ar', native: 'العربية',          hue: '#F5E1A8', dir: 'rtl', long: false },
  // idx 12 · LONG · upper-left → shortest LONG name (Deutsch, 7 chars)
  { code: 'de', native: 'Deutsch',           hue: '#FFD97D', dir: 'ltr', long: true  },
];

const N = LANGUAGES.length; // 13
const RAY_STEP = 360 / N;   // ≈ 27.69°
// Rotate the whole wheel by half a step so no language sits exactly at the
// top or bottom axis — this keeps the labels clear of the header/footer.
const RAY_OFFSET = -90 + RAY_STEP / 2;

// Distances from the geometric centre (in units of 10px on a 1000×1000 SVG
// canvas → the values below are effectively %). Long rays reach further
// out, giving room for the longer language names; short rays stay tucked
// in — the alternation adds visual rhythm. The inner radius is now larger
// because the central Falak icon takes more space than the drawn crescent
// it replaced.
const RAY_INNER      = 15;   // where every ray starts (just outside the icon)
const RAY_OUTER_LONG = 28;   // where a LONG ray ends
const RAY_OUTER_SHORT = 20;  // where a SHORT ray ends
// Label sits a small margin beyond the tip of its ray. The gap is larger
// now because the labels themselves are larger.
const LABEL_GAP_LONG  = 8;
const LABEL_GAP_SHORT = 8;
// Vertical squish factor for the elliptical label ring — smaller = more
// compact vertically, keeping labels visually anchored to the icon on
// portrait phones (9:16).
const VERTICAL_SQUISH = 0.72;

export default function LanguagesShowcase() {
  // Load the multilingual Google fonts we need for correct shaping of
  // Arabic-family scripts (Amiri), Bengali (Noto Sans Bengali) and CJK
  // (Noto Sans SC). Done via a runtime <link> injection so the showcase
  // route is fully self-contained.
  React.useEffect(() => {
    const links = [
      'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Noto+Sans+Bengali:wght@400;500;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&display=swap',
    ];
    const added = links.map((href) => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
      return l;
    });
    return () => {
      added.forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at center, #0B1428 0%, #050914 55%, #020610 100%)',
        color: '#F5E9C8',
        fontFamily: '"Cormorant Garamond", "Amiri", "Noto Serif", serif',
      }}
      data-testid="languages-showcase-root"
    >
      {/* Ambient star field */}
      <StarField />

      {/* Subtle vignetting */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Top caption */}
      <div
        style={{
          position: 'absolute',
          top: '6.5%',
          left: 0,
          right: 0,
          textAlign: 'center',
          padding: '0 6%',
        }}
      >
        <div
          style={{
            fontSize: '15px',
            letterSpacing: '0.32em',
            color: '#E0C48C',
            textTransform: 'uppercase',
            opacity: 0.85,
          }}
        >
          Falak
        </div>
        <div
          style={{
            marginTop: 12,
            fontSize: '32px',
            fontWeight: 400,
            letterSpacing: '0.02em',
            color: '#F5E9C8',
            lineHeight: 1.2,
          }}
        >
          One sky · Thirteen tongues
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: '18px',
            fontWeight: 300,
            letterSpacing: '0.04em',
            color: 'rgba(245,233,200,0.65)',
            direction: 'rtl',
          }}
        >
          سماءٌ واحدة · ثلاثَ عشرةَ لُغَة
        </div>
      </div>

      {/* Ray container — a square SVG that always fits the viewport width */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          viewBox="0 0 1000 1000"
          preserveAspectRatio="xMidYMid meet"
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100vh',
            maxHeight: '100vh',
          }}
        >
          <defs>
            {LANGUAGES.map((lang, i) => {
              const outerEnd = lang.long ? RAY_OUTER_LONG : RAY_OUTER_SHORT;
              return (
                <linearGradient
                  key={lang.code}
                  id={`ray-${lang.code}`}
                  gradientUnits="userSpaceOnUse"
                  x1="500" y1="500"
                  x2={500 + Math.cos((i * RAY_STEP + RAY_OFFSET) * Math.PI / 180) * outerEnd * 10}
                  y2={500 + Math.sin((i * RAY_STEP + RAY_OFFSET) * Math.PI / 180) * outerEnd * 10}
                >
                  <stop offset="0%"  stopColor={lang.hue} stopOpacity="1" />
                  <stop offset="55%" stopColor={lang.hue} stopOpacity="0.55" />
                  <stop offset="100%" stopColor={lang.hue} stopOpacity="0" />
                </linearGradient>
              );
            })}

            {/* Crescent gradient */}
            <radialGradient id="crescent-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#FFE5A0" stopOpacity="1" />
              <stop offset="55%" stopColor="#E0C48C" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#8B6E3D" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Rays as thick tapered lines — LONG or SHORT alternating */}
          {LANGUAGES.map((lang, i) => {
            const angleDeg = i * RAY_STEP + RAY_OFFSET;
            const rad = (angleDeg * Math.PI) / 180;
            const outerEnd = lang.long ? RAY_OUTER_LONG : RAY_OUTER_SHORT;
            const x1 = 500 + Math.cos(rad) * (RAY_INNER * 10);
            const y1 = 500 + Math.sin(rad) * (RAY_INNER * 10);
            const x2 = 500 + Math.cos(rad) * (outerEnd * 10);
            const y2 = 500 + Math.sin(rad) * (outerEnd * 10);
            return (
              <line
                key={`ray-line-${lang.code}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={`url(#ray-${lang.code})`}
                strokeWidth="10"
                strokeLinecap="round"
              />
            );
          })}

          {/* Faint outer ring that hugs the LONG rays — subtle geometry */}
          <circle
            cx="500" cy="500" r={RAY_OUTER_LONG * 10 + 4}
            fill="none"
            stroke="rgba(224,196,140,0.08)"
            strokeWidth="1"
            strokeDasharray="3 6"
          />

          {/* Central Falak app icon glow — subtle radial halo behind the icon */}
          <circle cx="500" cy="500" r="170" fill="url(#crescent-glow)" opacity="0.30" />
        </svg>

        {/* Falak app icon in the center — circular, no white bezel */}
        <img
          src="/icon-circle.png"
          alt="Falak"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'clamp(180px, 22vh, 260px)',
            height: 'clamp(180px, 22vh, 260px)',
            borderRadius: '50%',
            boxShadow:
              '0 0 60px rgba(240, 208, 138, 0.45), 0 0 140px rgba(240, 208, 138, 0.20)',
          }}
        />

        {/* Language labels — HTML for perfect script rendering */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
          }}
        >
          {LANGUAGES.map((lang, i) => {
            const angleDeg = i * RAY_STEP + RAY_OFFSET;
            const rad = (angleDeg * Math.PI) / 180;
            // Label sits just beyond the tip of its own ray. LONG rays push
            // their labels further out; SHORT rays keep them close in.
            const outerEnd = lang.long ? RAY_OUTER_LONG : RAY_OUTER_SHORT;
            const labelGap = lang.long ? LABEL_GAP_LONG : LABEL_GAP_SHORT;
            const rX = outerEnd + labelGap;                    // horizontal radius (%)
            const rY = (outerEnd + labelGap) * VERTICAL_SQUISH; // vertical radius (compressed for portrait)
            const x = 50 + Math.cos(rad) * rX;
            const y = 50 + Math.sin(rad) * rY;
            // RTL scripts (Arabic, Urdu, Persian) and complex-shaping scripts
            // (Bengali, CJK) break their glyph-joining when letter-spacing
            // is applied. Only add spacing on plain Latin scripts.
            const isRTL = lang.dir === 'rtl';
            const isCJK = lang.code === 'zh' || lang.code === 'bn';
            const allowSpacing = !isRTL && !isCJK;
            return (
              <div
                key={`label-${lang.code}`}
                style={{
                  position: 'absolute',
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: lang.hue,
                  fontSize: 'clamp(20px, 3.2vh, 34px)',
                  fontWeight: 600,
                  letterSpacing: allowSpacing ? '0.02em' : 'normal',
                  textShadow: '0 2px 10px rgba(0,0,0,0.65), 0 0 24px rgba(0,0,0,0.5)',
                  direction: lang.dir,
                  whiteSpace: 'nowrap',
                  filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.4))',
                  fontFamily: isRTL
                    ? '"Amiri", "Noto Sans Arabic", "Scheherazade New", "Traditional Arabic", serif'
                    : isCJK
                    ? (lang.code === 'zh' ? '"Noto Sans SC", sans-serif' : '"Noto Sans Bengali", sans-serif')
                    : '"Cormorant Garamond", "Noto Serif", serif',
                }}
              >
                {lang.native}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom caption */}
      <div
        style={{
          position: 'absolute',
          bottom: '5%',
          left: 0,
          right: 0,
          textAlign: 'center',
          padding: '0 6%',
        }}
      >
        <div
          style={{
            fontSize: '19px',
            fontWeight: 300,
            letterSpacing: '0.04em',
            color: 'rgba(245,233,200,0.75)',
          }}
        >
          Free · No ads · No tracking
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: '15px',
            color: 'rgba(245,233,200,0.45)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          falakmoon.com
        </div>
      </div>
    </div>
  );
}

// A small deterministic star sprinkling so the background feels alive
// but doesn't dance between renders.
function StarField() {
  const stars = React.useMemo(() => {
    const rng = mulberry32(0xFA1AC);
    return Array.from({ length: 80 }, () => ({
      x: rng() * 100,
      y: rng() * 100,
      size: 0.6 + rng() * 1.6,
      opacity: 0.25 + rng() * 0.55,
    }));
  }, []);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {stars.map((s, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: '50%',
            background: '#F5E9C8',
            opacity: s.opacity,
            boxShadow: `0 0 ${s.size * 2}px rgba(245,233,200,${s.opacity * 0.6})`,
          }}
        />
      ))}
    </div>
  );
}

// Tiny seeded PRNG so the star field is stable across mounts.
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}
